/**
 * AndroidTesters — the public sign-up page for the Google Play closed test,
 * at /android-testers, the one link shared on Facebook and WhatsApp.
 *
 * Google only lets a new personal developer account publish once at least 12
 * testers have stayed in a closed test for 14 days, and a closed test admits
 * only Google accounts on the owner's tester list. So this page collects the
 * Google account email (plus an optional name and WhatsApp number) with POPIA
 * consent and posts it to /auth/tester-signup/ with `program: "play"`. The
 * server alerts the owner by email and WhatsApp; nothing is granted yet. The
 * owner adds the email in Play Console, then taps "Added to Play" on Revenue ->
 * Testers, which grants the free months and emails the tester the join link.
 * The "6 months" copy matches settings.ANDROID_TESTER_DAYS (183); change both
 * together. Styling reuses the home page's Nocturne classes in vault-home.css.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import './vault-home.css';

const STEPS = [
  ['1', 'Leave the Google account email you use for the Play Store on your Android phone.'],
  ['2', 'I add you to the test and email you the join link.'],
  ['3', 'Open the link on your phone, become a tester and install Vault from the Play Store.'],
  ['4', 'Sign in to Vault with that same email and keep it installed for at least 14 days. Your 6 months free are on that email.'],
  ['5', 'Have a Wear OS watch? Vault has a watch app too, and it needs its own 12 testers. The same email gets you its link.'],
];

/**
 * The page: what Google needs, the five steps (the last about the Wear OS
 * watch app), and the sign-up form. After a
 * successful post it swaps the form for a confirmation naming the email, so a
 * typo is easy to spot and send again.
 */
export default function AndroidTesters() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [err, setErr] = useState('');

  /** Post the sign-up; the server validates the email and the consent too. */
  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!consent) { setErr('Please tick the box so I may contact you about the test.'); return; }
    setBusy(true);
    try {
      await api('/auth/tester-signup/', {
        method: 'POST',
        body: { program: 'play', name, email, phone, consent: true },
      });
      setDone(email.trim().toLowerCase());
    } catch (ex) {
      setErr(ex?.data?.detail || 'Could not sign you up. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vhome vh-page">
      <header className="vh-nav">
        <Link to="/" className="vh-brand" aria-label="Vault home">
          <img src="/logo.svg" alt="" />
          <span className="vh-brand-word">Vault</span>
        </Link>
      </header>

      <section className="vh-section vh-tester">
        <div className="vh-tester-card">
          <span className="vh-kicker">Android testers · 6 months free</span>
          <h1 className="vh-tester-title vh-play-title">
            Help me get Vault onto Google Play &mdash; <span className="hl">6 months free</span>
          </h1>
          <p className="vh-tester-sub">
            Vault is a payday-first budgeting app built in South Africa. Google only lets me
            publish it on the Play Store once at least 12 people have tested it for 14 days.
            Join the test and Vault is free for you for 6 months.
          </p>

          <div className="vh-refer-steps vh-play-steps">
            {STEPS.map(([n, b]) => (
              <div className="vh-refer-step" key={n}>
                <div className="n">{n}</div>
                <div className="b">{b}</div>
              </div>
            ))}
          </div>

          {done ? (
            <div className="vh-tester-done" role="status">
              <span className="vh-tester-done-badge" aria-hidden="true">&#10003;</span>
              <div>
                Thank you{name.trim() ? `, ${name.trim()}` : ''}. I&rsquo;ll add <strong>{done}</strong> to
                the Google Play test and email you the join link. Wrong email?{' '}
                <button type="button" className="vh-link-btn" onClick={() => setDone('')}>
                  Send it again
                </button>
              </div>
            </div>
          ) : (
            <form className="vh-tester-form" onSubmit={submit}>
              <div className="vh-tester-fields">
                <input className="vh-input" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Your name" aria-label="Your name" autoComplete="name" />
                <input className="vh-input" type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       placeholder="Your Play Store email"
                       aria-label="Google account email used on the Play Store" autoComplete="email" />
                <input className="vh-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="WhatsApp number (optional)" aria-label="WhatsApp number"
                       autoComplete="tel" inputMode="tel" />
              </div>
              <label className="vh-tester-check">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>I agree to be contacted about the Vault test (POPIA). My email is only used to add me to it.</span>
              </label>
              <button type="submit" disabled={busy}
                      className="vh-btn vh-btn-primary vh-btn-lg vh-tester-submit">
                {busy ? 'Signing you up…' : 'Sign me up for the Android test'}
              </button>
            </form>
          )}
          {err && <div className="vh-wait-err" role="alert">{err}</div>}
          <div className="vh-tester-foot">
            Not on Android? <Link to="/">See Vault on the web and the App Store &rarr;</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
